import { noop } from 'lodash';
import { memo, type ReactNode, useCallback } from 'react';

import { useCommandState } from '@air/cmdk';

import { SearchCommandGroup } from '~/components/SearchCommand/SearchCommandGroup';
import { SearchCommandListFrame } from '~/components/SearchCommand/SearchCommandListFrame';
import { SearchCommandSuggestionListItem } from '~/components/SearchCommand/SearchCommandSuggestionListItem';
import { SmartSearchPromotionDropdown } from '~/components/SearchCommand/SmartSearchPromotionDropdown';
import { getHasSeenSmartSearchPromotion } from '~/utils/SearchUtils';

export type SmartSearchCommandListProps = {
  isOpen: boolean;
  listStart: ReactNode | undefined;
  onSelect?: (suggestion: string) => void;
};

export const SmartSearchCommandList = memo(({ isOpen, listStart, onSelect }: SmartSearchCommandListProps) => {
  const search = useCommandState((state) => state.search);

  const showSmartSearchPromotion = !getHasSeenSmartSearchPromotion() && isOpen;

  const handleSelectSearch = useCallback(() => {
    onSelect?.(search);
  }, [onSelect, search]);

  if (search) {
    return (
      <SearchCommandListFrame>
        <SearchCommandGroup>
          <SearchCommandSuggestionListItem isCurrentQuery onSelect={handleSelectSearch} suggestion={search} />
        </SearchCommandGroup>
        {listStart}
      </SearchCommandListFrame>
    );
  }

  if (showSmartSearchPromotion) {
    // TODO: Follow-up in https://linear.app/air/issue/COR-1149/add-learn-more-mechanism-to-smart-search-promotional-dropdown
    return (
      <div className="flex max-h-[min(480px,calc(100vh-8rem))] min-h-0 grow flex-col overflow-y-auto ads:border-ads-gray-5 border-gray-5 border-t">
        <SmartSearchPromotionDropdown onLearnMoreClick={noop} />
      </div>
    );
  }

  return null;
});

SmartSearchCommandList.displayName = 'SmartSearchCommandList';
